import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { format, subDays } from 'date-fns';
import DateQuickPresets from '../../components/DateQuickPresets';

const PainelExecutivo = () => {
  const hoje = new Date();
  const [carregando, setCarregando] = useState(false);
  const [centrosCustos, setCentrosCustos] = useState([]);
  const [contasBancarias, setContasBancarias] = useState([]);
  const [filtros, setFiltros] = useState({
    data_inicio: format(subDays(hoje, 30), 'yyyy-MM-dd'),
    data_fim: format(hoje, 'yyyy-MM-dd'),
    regime: 'competencia',
    centro_custo_id: 'todos',
    conta_bancaria_id: 'todas',
  });
  const [dados, setDados] = useState({
    variacoes: {
      receitas: { atual: 0, anterior: 0, variacao_abs: 0, variacao_pct: null },
      despesas: { atual: 0, anterior: 0, variacao_abs: 0, variacao_pct: null },
      resultado: { atual: 0, anterior: 0, variacao_abs: 0, variacao_pct: null },
    },
    top_despesas: [],
    concentracao_receitas: [],
    alertas: [],
  });

  const formatarValor = (value) =>
    Number(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const formatarPct = (value) => (value == null ? '-' : `${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}%`);

  const carregarFiltros = async () => {
    try {
      const [centrosResponse, contasResponse] = await Promise.all([
        axios.get('/api/centro-custos'),
        axios.get('/api/contas-bancarias'),
      ]);
      setCentrosCustos(Array.isArray(centrosResponse.data) ? centrosResponse.data : []);
      setContasBancarias(Array.isArray(contasResponse.data) ? contasResponse.data : []);
    } catch (error) {
      console.error('Erro ao carregar filtros do painel executivo:', error);
    }
  };

  const carregarDados = async () => {
    setCarregando(true);
    try {
      const response = await axios.get('/api/painel-executivo', { params: filtros });
      const payload = response.data || {};
      setDados({
        variacoes: payload.variacoes || {
          receitas: { atual: 0, anterior: 0, variacao_abs: 0, variacao_pct: null },
          despesas: { atual: 0, anterior: 0, variacao_abs: 0, variacao_pct: null },
          resultado: { atual: 0, anterior: 0, variacao_abs: 0, variacao_pct: null },
        },
        top_despesas: Array.isArray(payload.top_despesas) ? payload.top_despesas : [],
        concentracao_receitas: Array.isArray(payload.concentracao_receitas) ? payload.concentracao_receitas : [],
        alertas: Array.isArray(payload.alertas) ? payload.alertas : [],
      });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar painel executivo');
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    carregarFiltros();
  }, []);

  useEffect(() => {
    carregarDados();
  }, [filtros]);

  return (
    <div className="animate-fade-in">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Painel Executivo</h1>
          <p className="text-gray-600">Top despesas, concentração de receitas, variação e alertas</p>
        </div>
      </div>

      <div className="card mb-6">
        <div className="card-body">
          <DateQuickPresets
            mode="range"
            startDate={filtros.data_inicio}
            endDate={filtros.data_fim}
            onSelect={({ startDate, endDate }) =>
              setFiltros((old) => ({ ...old, data_inicio: startDate, data_fim: endDate }))
            }
            className="mb-4"
          />

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <div>
              <label className="form-label">Data Inicial</label>
              <input type="date" className="form-input" value={filtros.data_inicio} onChange={(e) => setFiltros((old) => ({ ...old, data_inicio: e.target.value }))} />
            </div>
            <div>
              <label className="form-label">Data Final</label>
              <input type="date" className="form-input" value={filtros.data_fim} onChange={(e) => setFiltros((old) => ({ ...old, data_fim: e.target.value }))} />
            </div>
            <div>
              <label className="form-label">Regime</label>
              <select className="form-select" value={filtros.regime} onChange={(e) => setFiltros((old) => ({ ...old, regime: e.target.value }))}>
                <option value="competencia">Competencia</option>
                <option value="caixa">Caixa</option>
              </select>
            </div>
            <div>
              <label className="form-label">Centro de Custo</label>
              <select className="form-select" value={filtros.centro_custo_id} onChange={(e) => setFiltros((old) => ({ ...old, centro_custo_id: e.target.value }))}>
                <option value="todos">Todos</option>
                {centrosCustos.map((centro) => (
                  <option key={centro.id} value={centro.id}>{centro.codigo} - {centro.descricao}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">Conta Bancaria</label>
              <select className="form-select" value={filtros.conta_bancaria_id} onChange={(e) => setFiltros((old) => ({ ...old, conta_bancaria_id: e.target.value }))}>
                <option value="todas">Todas</option>
                {contasBancarias.map((conta) => (
                  <option key={conta.id} value={conta.id}>{conta.banco} - {conta.conta}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {['receitas', 'despesas', 'resultado'].map((k) => {
          const row = dados.variacoes?.[k] || {};
          const isPos = Number(row.variacao_abs || 0) >= 0;
          return (
            <div className="card" key={k}>
              <div className="card-body">
                <p className="text-sm text-gray-500 capitalize">{k}</p>
                <p className={`text-2xl font-bold ${k === 'despesas' ? 'text-danger-700' : 'text-success-700'}`}>R$ {formatarValor(row.atual)}</p>
                <p className={`text-xs ${isPos ? 'text-success-700' : 'text-danger-700'}`}>
                  Var: R$ {formatarValor(row.variacao_abs)} ({formatarPct(row.variacao_pct)})
                </p>
              </div>
            </div>
          );
        })}
      </div>

      <div className="card mb-6">
        <div className="card-header flex justify-between items-center">
          <h3 className="text-lg font-semibold">Alertas</h3>
          {carregando && <div className="loader h-5 w-5" />}
        </div>
        <div className="card-body">
          {(dados.alertas || []).length === 0 ? (
            <p className="text-sm text-success-700">Nenhum alerta crítico para o período.</p>
          ) : (
            <div className="space-y-2">
              {dados.alertas.map((a, idx) => (
                <div key={`${a.codigo}-${idx}`} className={`p-3 rounded border ${a.tipo === 'danger' ? 'bg-danger-50 border-danger-200 text-danger-700' : 'bg-warning-50 border-warning-200 text-warning-700'}`}>
                  {a.mensagem}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold">Top 10 Despesas</h3>
          </div>
          <div className="card-body p-0">
            <div className="table-container">
              <table className="table">
                <thead><tr><th>Conta</th><th>Grupo</th><th>Valor</th></tr></thead>
                <tbody>
                  {(dados.top_despesas || []).map((d) => (
                    <tr key={d.plano_conta_id}>
                      <td>{d.codigo} - {d.descricao}</td>
                      <td>{d.dre_grupo || '-'}</td>
                      <td className="text-danger-700">R$ {formatarValor(d.valor)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold">Concentração de Receitas</h3>
          </div>
          <div className="card-body p-0">
            <div className="table-container">
              <table className="table">
                <thead><tr><th>Conta</th><th>Grupo</th><th>Valor</th><th>%</th></tr></thead>
                <tbody>
                  {(dados.concentracao_receitas || []).map((r) => (
                    <tr key={r.plano_conta_id}>
                      <td>{r.codigo} - {r.descricao}</td>
                      <td>{r.dre_grupo || '-'}</td>
                      <td className="text-success-700">R$ {formatarValor(r.valor)}</td>
                      <td>{formatarPct(r.concentracao_pct)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PainelExecutivo;
